import * as React from 'react';
import { Helper } from '../../../../Helper';
import { IEnvironment } from '../../../../interfaces/IEnvironment';
import { PlayHelper } from '../../../playAdmin/PlayHelper';
import { Image } from '../image/Image';
import * as styles from './Video.css';
import { IndexDbFileService } from '../../../../services/IndexDbFileService';
import YouTube from 'react-youtube';
import type Player from '@vimeo/player';

export interface IProps {
  type: "video" | "youtube" | "vimeo" | "video23" | "screen9";
  videoUrl: string;
  width: number | string;
  height: number | string;
  position?: "absolute" | "relative";
  marginLeft?: number | string;
  marginTop?: number | string;
  backgroundColor?: string;
  showTitle?: boolean;
  title?: string;
  playerHeight?: number;
  playerWidth?: number;
  environment: IEnvironment;
  tenantId: string;
  profileId: string;
  willRestart: () => void;
  autoPlay?: boolean;
  style?: React.CSSProperties;
  onDuration?: (duration: number) => void;
  onError?: () => void;
}

export interface IState {
  isLoading: boolean;
  isKiosk: boolean;
  isPlaying: boolean;
  videoUrl?: string;
  videoRef: React.RefObject<HTMLVideoElement>;
}

export class Video extends React.Component<IProps, IState> {
  private indexDbFileService: IndexDbFileService = new IndexDbFileService();
  private videoInterval: ReturnType<typeof setInterval> | undefined;
  private vimeoRef = React.createRef<HTMLIFrameElement>();
  private vimeoPlayer: Player | undefined;

  constructor(props: IProps) {
    super(props);
    this.state = {
      isLoading: true,
      isKiosk: PlayHelper.isReplayKiosk(),
      isPlaying: props.autoPlay == undefined ? true : props.autoPlay,
      videoUrl: undefined,
      videoRef: React.createRef<HTMLVideoElement>()
    };
  }

  public componentDidMount(): void {
    if (this.props.type !== "screen9" && this.props.type !== "youtube") {
      this.indexDbFileService.getCachedFileUrl(this.props.videoUrl).then((newUrl) => {
        this.setState({ isLoading: false, videoUrl: newUrl });
      })
    } else if (this.props.type === "youtube") {
      this.setState({ isLoading: false, videoUrl: this.props.videoUrl });
    } else {
      this.handleScreen9();
    }
  };

  public componentWillReceiveProps(props: IProps): void {
    if (props.videoUrl !== this.props.videoUrl) {
      this.setState({ videoUrl: props.videoUrl });
    }
  }

  public componentDidUpdate(_prevProps: Readonly<IProps>, prevState: Readonly<IState>): void {
    // Initialize Vimeo player once the iframe is rendered
    if (prevState.isLoading && !this.state.isLoading &&
      (this.props.type === "vimeo" || this.state.videoUrl?.includes("vimeo.com") && this.state.videoUrl?.includes("vimeo.com"))) {
      this.initVimeoPlayer();
    }
    // Handle playing state changes
    if (this.state.isPlaying !== prevState.isPlaying && this.state.videoRef.current) {
      if (this.state.isPlaying) {
        this.state.videoRef.current.play().catch(() => {
          // Handle play promise rejection
        });
      } else {
        this.state.videoRef.current.pause();
      }
    }
  }

  private initVimeoPlayer(): void {
    if (!this.vimeoRef.current || !this.state.videoUrl?.includes("vimeo.com")) return;
    // Use require directly to get the UMD constructor without esModuleInterop wrapping
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const VimeoPlayer: new (el: HTMLIFrameElement) => Player = require('@vimeo/player');
    this.vimeoPlayer = new VimeoPlayer(this.vimeoRef.current);
    this.vimeoPlayer.getDuration().then((duration) => {
      if (duration && this.props.onDuration) {
        this.props.onDuration(duration);
      }
    }).catch(() => { });
    this.vimeoPlayer.on('ended', () => {
      this.props.willRestart();
      // The vimeo iframe isn't wired to state.isPlaying, so it must be
      // restarted directly through the Vimeo player API (otherwise a
      // single-slide playlist, whose slide/widget never remounts, would
      // leave the video frozen on its last frame instead of looping).
      this.vimeoPlayer?.setCurrentTime(0).then(() => this.vimeoPlayer?.play()).catch(() => { });
    });
  }

  private handleScreen9(): void {
    // Get screen9 video url
    let videoUrl = "";
    let videoUrlArrray = this.props.videoUrl?.split('"embedUrl": ');
    if (videoUrlArrray?.length > 1) {
      videoUrlArrray = videoUrlArrray[1].split('}</script>');
    }
    if (videoUrlArrray?.length > 1) {
      videoUrl = videoUrlArrray[0]?.replace(/"/g, "");
    }
    if (!videoUrl?.includes("autoplay=1")) {
      videoUrl += "?autoplay=1";
    }
    this.setState({ isLoading: false, videoUrl });
    // Get screen9 video duration
    let duration = 0;
    let seconds = 0;
    let minutes;
    let durationArrray = this.props.videoUrl?.split('"duration": "PT');
    if (durationArrray?.length > 1 && durationArrray[1]?.includes('M') && durationArrray[1]?.includes('S')) {
      minutes = parseInt(durationArrray[1].split('M')[0]);
      seconds = parseInt(durationArrray[1].split('M')[1].replace('S', ''));
    }
    if (minutes) {
      duration = minutes * 60;
    }
    if (seconds) {
      duration += seconds;
    }
    this.props?.onDuration?.(duration);
  }

  private handleUrl(): string | undefined {
    if ((this.props.type === "vimeo" || this.state.videoUrl?.includes("vimeo.com")) && !this.props.videoUrl?.includes("?")) {
      return `${this.props.videoUrl}?autoplay=1&muted=1&controls=0&title=0&byline=0&portrait=0`;
    } else {
      return this.state.videoUrl;
    }
  }

  private renderPlayer(style: React.CSSProperties): JSX.Element {
    if (this.props.type === "screen9") {
      return (
        <iframe
          id="IA_Iframe_ym9u9e_ube2ei"
          width="100%"
          height="100%"
          allow="autoplay"
          referrerPolicy="strict-origin-when-cross-origin"
          src={this.handleUrl()
          }
          style={{ float: "none" }}
        />
      );
    } else if (this.state.videoUrl && (this.props.type === "youtube" || this.state.videoUrl?.includes("www.youtube.com") && Helper.getYouTubeVideoIdFromURL(this.state.videoUrl!))) {
      return (
        <YouTube
          videoId={Helper.getYouTubeVideoIdFromURL(this.state.videoUrl!)}
          opts={{
            width: this.props.width,
            height: this.props.height,
            referrerPolicy: "strict-origin-when-cross-origin",
            playerVars: {
              autoplay: this.state.isPlaying ? 1 : 0,
              controls: 0,
              showinfo: 0,
              rel: 0,
              mute: 1,
              loop: 1,
              playlist: Helper.getYouTubeVideoIdFromURL(this.state.videoUrl!),
            },
          }}
          onReady={(event: any) => {
            const duration = event.target.getDuration();
            if (duration && this.props.onDuration) {
              this.props.onDuration(duration);
            }
          }}
          onEnd={() => {
            this.props.willRestart()
            this.setState({ isPlaying: false }, () => {
              setTimeout(() => {
                this.setState({ isPlaying: true });
              }, 100);
            })
          }}
          onError={() => {
            console.log("Video failed");
            this.props.onError && this.props.onError();
          }}
        />
      );
    } else if (this.props.type === "vimeo" || this.state.videoUrl?.includes("vimeo.com")) {
      return (
        <iframe
          ref={this.vimeoRef}
          id="IA_Iframe_ym9u9e_ube2ei"
          width="100%"
          height="100%"
          allow="autoplay"
          referrerPolicy="strict-origin-when-cross-origin"
          src={this.handleUrl()}
          style={{ float: "none" }}
        />
      );
    } else {
      return (
        <video
          ref={this.state.videoRef}
          className="react-player"
          src={this.state.videoUrl}
          width={this.props.width}
          height={this.props.height}
          muted={!this.state.isKiosk}
          style={style}
          autoPlay={this.state.isPlaying}
          disablePictureInPicture
          controlsList="nodownload noplaybackrate"
          onCanPlay={() => this.setState({ isLoading: false })}
          onError={() => {
            console.log("Video failed");
            this.props.onError && this.props.onError();
          }}
          onEnded={() => {
            this.props.willRestart()
            this.setState({ isPlaying: false }, () => {
              setTimeout(() => {
                this.setState({ isPlaying: true });
              }, 100);
            })
          }}
          onLoadedMetadata={(e) => {
            const video = e.currentTarget;
            if (this.props.onDuration && video.duration) {
              this.props.onDuration(video.duration);
            }
          }}
        />
      );
    }
  }

  public render(): JSX.Element {
    const style: React.CSSProperties = {
      position: this.props.position ?? "absolute",
      marginLeft: this.props.marginLeft ?? 0,
      marginTop: this.props.marginTop ?? 0,
      backgroundColor: this.props.backgroundColor ? this.props.backgroundColor : "transparent",
      ...this.props.style
    }
    return (
      <>
        {!this.state.isLoading &&
          this.renderPlayer(style)
        }
        {this.state.isLoading && this.props.videoUrl &&
          <>
            {this.props.type === "youtube"
              ?
              <div>
                <Image
                  imageUrl={Helper.getYouTubeThumbnailFromURL(this.props.videoUrl, "maxresdefault")}
                  isActive={true}
                  width={this.props.width}
                  height={this.props.height}
                  environment={this.props.environment}
                  profileId={this.props.profileId}
                  tenantId={this.props.tenantId}
                  backgroundColor={this.props.backgroundColor ? this.props.backgroundColor : "transparent"}
                />
              </div>
              :
              <div
                style={{
                  width: this.props.width,
                  height: this.props.height,
                  ...style
                }}
                className={styles.IA_videoSkeleton}
              />
            }
          </>
        }
      </>
    );
  }

  public componentWillUnmount(): void {
    clearInterval(this.videoInterval);
    this.vimeoPlayer?.destroy();
  }

}